import {
  NavLink as RouterLink,
  matchPath,
  useLocation,
} from "react-router-dom";
import PropTypes from "prop-types";
import { Box, Button, Collapse, List, ListItem } from "@material-ui/core";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@material-ui/icons";
import theme from "src/theme";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  item: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
  },
  link: {
    justifyContent: "flex-start",
    letterSpacing: 0,
    textTransform: "none",
    width: "100%",
    height: 40,
    "& svg": {
      fontSize: 20,
      marginRight: 8,
    },
  },
  subLink: {
    color: theme.palette.text.primary,
    marginLeft: "4px",
    position: "relative",

    "&:hover": {
      color: theme.palette.common.principal,
    },

    "&::before": {
      content: '""',

      position: "absolute",
      top: "17px",
      left: "0px",
      width: "10px",
      height: "1px",
      background: theme.palette.primary.main,
    },
  },
  linkActive: {
    marginLeft: 1,
    color: theme.palette.common.principal,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  subLinkActive: {
    color: theme.palette.common.principal,
    "&::before": {
      content: '""',

      position: "absolute",
      top: "17px",
      left: "0px",
      width: "10px",
      height: "1px",
      background: theme.palette.common.principal,
    },
  },
  dropdownButton: {
    position: "relative",
    "&::before": {
      content: '""',

      position: "absolute",
      top: "0",
      left: "2px",
      width: "2px",
      height: "calc(100% - 25px)",
      background: theme.palette.primary.main,
    },
  },
}));

const NavItem = ({
  disabled,
  href,
  icon: Icon,
  title,
  subitems = [],
  subitemsVisible = "",
  setSubitemsVisible = () => {},
  selectedSubItem = "",
  setSelectedSubItem = () => {},
  ...rest
}) => {
  const location = useLocation();
  const classes = useStyles();

  const active = href
    ? !!matchPath(
        {
          path: href,
          end: false,
        },
        location.pathname
      )
    : false;

  const handleToggleChildren = (item) => {
    if (item !== subitemsVisible) {
      setSubitemsVisible(item);
    } else {
      setSubitemsVisible(null);
    }
  };

  const handleSelectSubItem = (item, position) => setSelectedSubItem(item);

  return (
    <ListItem
      className={clsx(
        classes.item,
        subitemsVisible === title && classes.dropdownButton
      )}
      disabled={disabled}
      disableGutters
      {...rest}
    >
      {subitems.length ? (
        <>
          <Button
            className={clsx(
              classes.link,
              subitemsVisible === title && classes.linkActive
            )}
            onClick={() => handleToggleChildren(title)}
          >
            <Icon />
            <span>{title}</span>
            <Box marginLeft="auto">
              {subitemsVisible === title ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </Box>
          </Button>
          {subitems.map((item) => (
            <Collapse
              in={subitemsVisible === title ? true : false}
              key={item.title}
              timeout="auto"
              unmountOnExit
              style={{ width: "100%" }}
            >
              <List>
                <ListItem
                  className={clsx(
                    !item.disabled && classes.subLink,
                    selectedSubItem === item.title &&
                      !item.disabled &&
                      classes.subLinkActive
                  )}
                  component={RouterLink}
                  to={item.href}
                  onClick={() => handleSelectSubItem(item.title)}
                  disabled={item.disabled}
                >
                  {item.title}
                </ListItem>
              </List>
            </Collapse>
          ))}
        </>
      ) : (
        <Button
          component={RouterLink}
          className={clsx(classes.link, active && classes.linkActive)}
          to={href}
        >
          {Icon && <Icon size="20" />}
          <span style={{ marginLeft: 10 }}>{title}</span>
        </Button>
      )}
    </ListItem>
  );
};

NavItem.propTypes = {
  href: PropTypes.string,
  icon: PropTypes.elementType,
  title: PropTypes.string,
};

export default NavItem;
